import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Super-admin curation UI for `dashboard.pushdown.{connector_id, model_table_map}`
 * (iter-57 P1).
 *
 * Replaces the previous "Mongo-only" workflow — super-admins can now point a
 * prebuilt dashboard at a SQL connector and map each canonical model slug to
 * the real source table/schema. When valid, /joined-query routes to driver
 * pushdown (engine='sql_pushdown') instead of materialising via model_rows.
 */
export default function PushdownConfigDialog({ open, onOpenChange, dashboard, onSaved }) {
    const [connectors, setConnectors] = useState([]);
    const [connectorId, setConnectorId] = useState("");
    const [rows, setRows] = useState([]);  // [{slug, table, schema}]
    const [busy, setBusy] = useState(false);

    // Load eligible connectors (postgres / mysql / sqlalchemy_generic) and seed
    // the form from the dashboard's existing pushdown config.
    useEffect(() => {
        if (!open || !dashboard) return;
        api.get("/data/connectors").then((r) => {
            const eligible = ["postgresql", "mysql", "sqlalchemy_generic", "postgres"];
            const list = (r.data || []).filter((c) => eligible.includes((c.connector_key || "").toLowerCase()));
            setConnectors(list);
        }).catch(() => setConnectors([]));
        const pd = dashboard.pushdown || {};
        setConnectorId(pd.connector_id || "");
        const seedRows = Object.entries(pd.model_table_map || {}).map(([slug, e]) => ({
            slug, table: e.table || "", schema: e.schema || "",
        }));
        // Seed empty rows from data_model_bindings if no existing config.
        if (seedRows.length === 0) {
            const bindings = dashboard.data_model_bindings || [];
            bindings.forEach((b) => {
                const slug = (b.model_id || "").split(".").pop();
                if (slug) seedRows.push({ slug, table: "", schema: "" });
            });
        }
        setRows(seedRows.length > 0 ? seedRows : [{ slug: "", table: "", schema: "" }]);
    }, [open, dashboard]);

    const updateRow = (i, k, v) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
    const addRow = () => setRows((prev) => [...prev, { slug: "", table: "", schema: "" }]);
    const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

    const save = async () => {
        // Build map; drop rows where slug or table is empty.
        const map = {};
        rows.forEach((r) => {
            if (r.slug && r.table) map[r.slug] = { table: r.table, ...(r.schema ? { schema: r.schema } : {}) };
        });
        setBusy(true);
        try {
            const r = await api.patch(`/dashboards/${dashboard.id}/pushdown`, {
                connector_id: connectorId || null,
                model_table_map: Object.keys(map).length > 0 ? map : null,
            });
            toast.success(connectorId ? "Pushdown configured — joined queries will hit the source DB" : "Pushdown cleared");
            onSaved?.(r.data?.pushdown || null);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const clear = async () => {
        setBusy(true);
        try {
            await api.patch(`/dashboards/${dashboard.id}/pushdown`, { connector_id: null, model_table_map: null });
            toast.success("Pushdown cleared — joined queries will run via Mongo materialisation");
            onSaved?.(null);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Clear failed");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pushdown-config-dialog">
                <DialogHeader>
                    <DialogTitle>Configure SQL pushdown</DialogTitle>
                    <DialogDescription>
                        Route this prebuilt dashboard's joined queries directly to a SQL connector
                        (Postgres / MySQL / custom SQLAlchemy) instead of materialising via the
                        Mongo `model_rows` layer. Map each canonical model slug to its real source table.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div>
                        <label className="eyebrow mb-1 block">SQL connector</label>
                        <Select value={connectorId} onValueChange={setConnectorId}>
                            <SelectTrigger className="rounded-sm" data-testid="pushdown-connector-select">
                                <SelectValue placeholder="— pick a SQL connector —" />
                            </SelectTrigger>
                            <SelectContent>
                                {connectors.length === 0 && (
                                    <SelectItem value="__none" disabled>No eligible connectors. Add a Postgres / MySQL / SQLAlchemy connector first.</SelectItem>
                                )}
                                {connectors.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name || c.id} <span className="text-muted-foreground">({c.connector_key})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="eyebrow">Model → table map</label>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={addRow} data-testid="pushdown-add-row">
                                <Plus className="h-3 w-3 mr-1" /> Row
                            </Button>
                        </div>
                        <div className="space-y-1.5">
                            {rows.map((r, i) => (
                                <div key={i} className="grid grid-cols-[1.4fr_1.4fr_1fr_auto] gap-2 items-center"
                                     data-testid={`pushdown-row-${i}`}>
                                    <Input value={r.slug} onChange={(e) => updateRow(i, "slug", e.target.value)}
                                           placeholder="canonical slug (e.g. campaign)"
                                           className="rounded-sm font-mono text-xs"
                                           data-testid={`pushdown-row-slug-${i}`} />
                                    <Input value={r.table} onChange={(e) => updateRow(i, "table", e.target.value)}
                                           placeholder="real table name"
                                           className="rounded-sm font-mono text-xs"
                                           data-testid={`pushdown-row-table-${i}`} />
                                    <Input value={r.schema} onChange={(e) => updateRow(i, "schema", e.target.value)}
                                           placeholder="schema (optional)"
                                           className="rounded-sm font-mono text-xs"
                                           data-testid={`pushdown-row-schema-${i}`} />
                                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive"
                                            onClick={() => removeRow(i)} data-testid={`pushdown-remove-${i}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-2">
                            Slug examples: <code className="font-mono">campaign</code>, <code className="font-mono">ad_group</code>, <code className="font-mono">lead</code>.
                            Empty rows are dropped on save.
                        </div>
                    </div>
                </div>

                <DialogFooter className="flex items-center justify-between sm:justify-between">
                    <Button variant="ghost" className="rounded-sm text-destructive" onClick={clear} disabled={busy} data-testid="pushdown-clear">
                        Clear pushdown
                    </Button>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="pushdown-cancel">
                            Cancel
                        </Button>
                        <Button onClick={save} disabled={busy} variant="default"
                                className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
                                data-testid="pushdown-save">
                            {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</> : "Save pushdown"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
